import { useCallback } from "react";
import { useParams } from "react-router-dom";

import { useMutation } from "@apollo/client";
import { type LensCompoundId } from "../../../gql-generated/types";
import { GroupCache } from "../../groups/api/cache";
import { getGroupDetails } from "../../groups/api/queries/getGroupV2";
import { LensCache } from "../../lenses/api/cache";
import { createConnectionBulk } from "../api/mutation/createConnectionBulk";
import {
    type CreateConnectionBulkMutation,
    type CreateConnectionBulkMutationVariables,
} from "../api/mutation/createConnectionBulk.gql-generated";
import {
    type GroupConnection,
    GroupConnectionOperations,
    type LensConnection,
    LensConnectionOperations,
} from "../models";

export function useAddOrUpdateConnections() {
    const { orgId } = useParams();
    const [createConnections, { loading, error }] = useMutation<
        CreateConnectionBulkMutation,
        CreateConnectionBulkMutationVariables
    >(createConnectionBulk, {
        update: (cache, { data }) => {
            data?.createConnectionBulk?.connections?.forEach((connection) => {
                if (connection !== null) {
                    GroupCache.addConnectionIfNotExists(cache, connection);
                    LensCache.addNewConnection(cache, connection);
                }
            });
        },
    });

    const addOrUpdateGroupConnections = useCallback(
        (groupId: string, connections: readonly GroupConnection[]): Promise<unknown> =>
            connections.length === 0
                ? Promise.resolve([])
                : createConnections({
                      variables: {
                          createRequests: connections.map((connection) =>
                              GroupConnectionOperations.toVariables(orgId as string, groupId, connection),
                          ),
                      },
                      refetchQueries: [{ query: getGroupDetails, variables: { id: groupId, organizationId: orgId } }],
                  }),
        [createConnections, orgId],
    );

    const addOrUpdateLensConnections = useCallback(
        (lensCompoundId: LensCompoundId, connections: readonly LensConnection[]) =>
            connections.length === 0
                ? Promise.resolve([])
                : createConnections({
                      variables: {
                          createRequests: connections.map((connection) =>
                              LensConnectionOperations.toVariables(orgId as string, lensCompoundId, connection),
                          ),
                      },
                  }),
        [createConnections, orgId],
    );

    return {
        addOrUpdateConnections: addOrUpdateGroupConnections,
        addOrUpdateConnectionForLens: addOrUpdateLensConnections,
        loading,
        error,
    };
}
